import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const Login: React.FC = () => {
  const [email, setEmail] = useState('admin@school.edu');
  const [password, setPassword] = useState('admin123');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
  const { login } = useAuth();

  const handleBackToPassword = () => {
    setRequiresTwoFactor(false);
    setTwoFactorCode('');
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const credentials = requiresTwoFactor
        ? { email, password, twoFactorCode }
        : { email, password };

      const response = await login(credentials);

      // Handle 2FA requirement
      if (response && 'requiresTwoFactor' in response && response.requiresTwoFactor) {
        setRequiresTwoFactor(true);
        setTwoFactorCode('');
        setError('');
      }
      // If we get here and no 2FA required, login was successful
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
      // Reset 2FA state on error
      if (requiresTwoFactor) {
        setTwoFactorCode('');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600">
      <div className="max-w-md w-full space-y-8 p-8">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              🎓 School Admin
            </h1>
            <p className="text-gray-600">
              Lead Generation Platform
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            {!requiresTwoFactor ? (
              <>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your email"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your password"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="text-center mb-4">
                  <div className="text-lg font-medium text-gray-900 mb-2">
                    🔐 Two-Factor Authentication
                  </div>
                  <p className="text-sm text-gray-600">
                    Enter the 6-digit code from your authenticator app or a backup code
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Logging in as: {email}
                  </p>
                </div>

                <div>
                  <label htmlFor="twoFactorCode" className="block text-sm font-medium text-gray-700 mb-2">
                    Authentication Code
                  </label>
                  <input
                    id="twoFactorCode"
                    name="twoFactorCode"
                    type="text"
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    maxLength={8}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-lg tracking-wider"
                    placeholder="000000"
                    autoComplete="one-time-code"
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter 6-digit code or backup code
                  </p>
                </div>

                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={handleBackToPassword}
                    className="text-sm text-blue-600 hover:text-blue-500"
                  >
                    ← Back to password
                  </button>
                </div>
              </>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {requiresTwoFactor ? 'Verifying...' : 'Signing in...'}
                </div>
              ) : (
                requiresTwoFactor ? 'Verify Code' : 'Sign In'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <div className="text-sm text-gray-500">
              Default credentials for development:
            </div>
            <div className="text-xs text-gray-400 mt-1">
              Email: admin@school.edu<br />
              Password: admin123
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;